import { RcraInfoFederalWasteGeneratorStatus } from "generated-graphql/graphql";
import { DateTime } from "luxon";

/**
 * The federal central-accumulation window, in days, by generator status.
 *
 * VSQG is absent on purpose rather than mapped to a number: a very small
 * quantity generator has no federal time limit at all, and inventing one for
 * the preview would show a countdown the saved container will not have.
 */
const CENTRAL_ACCUMULATION_DAYS: Partial<Record<RcraInfoFederalWasteGeneratorStatus, number>> = {
  [RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator]: 90,
  [RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator]: 180,
};

/** An SQG shipping over 200 miles to its designated facility gets 270 days. */
const SQG_OVER_200_MILES_DAYS = 270;

const GENERATOR_STATUS_ABBREVIATION: Partial<Record<RcraInfoFederalWasteGeneratorStatus, string>> =
  {
    [RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator]: "LQG",
    [RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator]: "SQG",
    [RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator]: "VSQG",
  };

export type ClockProjection = {
  /** Length of the accumulation window. */
  totalDays: number;
  /** Whole days between today and the deadline; negative once it has passed. */
  daysRemaining: number;
  deadline: DateTime;
  /** "180 days — SQG, central accumulation". */
  basis: string;
};

/**
 * How long the container being drafted will have, so the form can show its
 * clock before the container exists.
 *
 * Deliberately a PREVIEW, not a second implementation of the clock: it reads
 * only the federal central-accumulation rule off the two inputs the form
 * already shows the user (the facility's generator status and the date the
 * container moves to central). The saved container's clock is computed by the
 * accumulation engine, which also applies state rules and satellite move
 * windows — so a state with a shorter window will show a nearer deadline on
 * the container than this projected here. Anything this can't state plainly
 * from those two inputs returns null and the rail says nothing rather than
 * guessing.
 */
export function projectCentralAccumulationClock({
  generatorStatus,
  movedToCentralDate,
  sqgOver200Miles,
  now = DateTime.local(),
}: {
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
  /** ISO date; the clock does not start until the container reaches central. */
  movedToCentralDate: string | null;
  sqgOver200Miles: boolean;
  now?: DateTime;
}): ClockProjection | null {
  if (!generatorStatus || !movedToCentralDate) {
    return null;
  }
  const baseDays = CENTRAL_ACCUMULATION_DAYS[generatorStatus];
  if (baseDays == null) {
    return null;
  }
  const isSqg = generatorStatus === RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator;
  const totalDays = isSqg && sqgOver200Miles ? SQG_OVER_200_MILES_DAYS : baseDays;

  const start = DateTime.fromISO(movedToCentralDate);
  if (!start.isValid) {
    return null;
  }
  const deadline = start.plus({ days: totalDays });
  // Day-granular, on calendar days rather than elapsed hours, so the number
  // matches the engine's own whole-day countdown.
  const daysRemaining = Math.round(deadline.startOf("day").diff(now.startOf("day"), "days").days);
  const abbreviation = GENERATOR_STATUS_ABBREVIATION[generatorStatus] ?? generatorStatus;

  return {
    totalDays,
    daysRemaining,
    deadline,
    basis: `${totalDays} days — ${abbreviation}, central accumulation`,
  };
}

/** "SQG — 180-day limit", the form's own label for the derived status. */
export function generatorStatusSummary(
  generatorStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined,
  sqgOver200Miles: boolean
): string | null {
  if (!generatorStatus) {
    return null;
  }
  const abbreviation = GENERATOR_STATUS_ABBREVIATION[generatorStatus] ?? generatorStatus;
  const baseDays = CENTRAL_ACCUMULATION_DAYS[generatorStatus];
  if (baseDays == null) {
    return `${abbreviation} — no federal storage limit`;
  }
  const isSqg = generatorStatus === RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator;
  const days = isSqg && sqgOver200Miles ? SQG_OVER_200_MILES_DAYS : baseDays;
  return `${abbreviation} — ${days}-day limit`;
}
